import { createTheme } from '@mui/material/styles';

// Modern color palette with gradients
const colors = {
  // Primary gradients
  primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  primaryGradientHover: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
  
  // Secondary gradients
  secondaryGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  secondaryGradientHover: 'linear-gradient(135deg, #e085e8 0%, #e54d62 100%)',
  
  // Success gradients
  successGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  successGradientHover: 'linear-gradient(135deg, #3e9bed 0%, #00e1ed 100%)',
  
  // Warning gradients
  warningGradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  warningGradientHover: 'linear-gradient(135deg, #e95f89 0%, #edcf30 100%)',
  
  // Error gradients
  errorGradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  errorGradientHover: 'linear-gradient(135deg, #ed898d 0%, #edbfdf 100%)',
  
  // Info gradients
  infoGradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  infoGradientHover: 'linear-gradient(135deg, #97dcd9 0%, #edc5d2 100%)',
  
  // Neutral colors
  white: '#ffffff',
  black: '#000000',
  transparent: 'transparent',
  
  // Glassmorphism colors
  glassBg: 'rgba(255, 255, 255, 0.1)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
  glassShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  
  // Dark mode glassmorphism
  darkGlassBg: 'rgba(0, 0, 0, 0.2)',
  darkGlassBorder: 'rgba(255, 255, 255, 0.1)',
  darkGlassShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
};

// Custom breakpoints
const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
  xxl: 2560,
};

// Typography scale
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  
  h1: {
    fontSize: '3.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '2.75rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '2.25rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontSize: '1.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
  },
  h6: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
  },
  subtitle1: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  subtitle2: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.05em',
    textTransform: 'none',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.02em',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
};

// Spacing system
const spacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  xxl: '3rem',
  xxxl: '4rem',
};

// Border radius system
const borderRadius = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
  round: '50%',
};

// Shadow system
const shadows = {
  xs: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
  sm: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
  md: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
  lg: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
  xl: '0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22)',
  glass: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  glassDark: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
};

// Animation durations
const transitions = {
  fast: '150ms',
  normal: '250ms',
  slow: '350ms',
  slower: '500ms',
};

// Create the modern theme
export const createModernTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#667eea',
        light: '#8fa4f1',
        dark: '#4c63d6',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#f093fb',
        light: '#f4b3fc',
        dark: '#e85dd8',
        contrastText: '#ffffff',
      },
      success: {
        main: '#4facfe',
        light: '#7bc4fe',
        dark: '#2d8fd6',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#fa709a',
        light: '#fb9bb8',
        dark: '#e85d8a',
        contrastText: '#ffffff',
      },
      error: {
        main: '#ff9a9e',
        light: '#ffb3b6',
        dark: '#e67a7e',
        contrastText: '#ffffff',
      },
      info: {
        main: '#a8edea',
        light: '#c0f2ef',
        dark: '#8dd8d5',
        contrastText: '#000000',
      },
      background: {
        default: isDark ? '#0a0a0a' : '#f8fafc',
        paper: isDark ? '#1a1a1a' : '#ffffff',
        glass: isDark ? colors.darkGlassBg : colors.glassBg,
      },
      text: {
        primary: isDark ? '#ffffff' : '#1a202c',
        secondary: isDark ? '#a0aec0' : '#4a5568',
        disabled: isDark ? '#718096' : '#a0aec0',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    },
    typography,
    breakpoints: {
      values: breakpoints,
    },
    shape: {
      borderRadius: parseInt(borderRadius.md),
    },
    shadows: [
      'none',
      shadows.xs,
      shadows.sm,
      shadows.md,
      shadows.lg,
      shadows.xl,
      // ... rest of the shadows array
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: isDark ? '#1a1a1a' : '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? '#333' : '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: isDark ? '#555' : '#a8a8a8',
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.md,
            textTransform: 'none',
            fontWeight: 600,
            transition: `all ${transitions.normal} ease-in-out`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: shadows.md,
            },
          },
          contained: {
            background: colors.primaryGradient,
            '&:hover': {
              background: colors.primaryGradientHover,
            },
          },
          outlined: {
            border: `2px solid ${isDark ? colors.darkGlassBorder : colors.glassBorder}`,
            background: isDark ? colors.darkGlassBg : colors.glassBg,
            backdropFilter: 'blur(10px)',
            '&:hover': {
              background: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            background: isDark ? colors.darkGlassBg : colors.glassBg,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? colors.darkGlassBorder : colors.glassBorder}`,
            boxShadow: isDark ? shadows.glassDark : shadows.glass,
            transition: `all ${transitions.normal} ease-in-out`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: isDark ? shadows.lg : shadows.xl,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.md,
            background: isDark ? colors.darkGlassBg : colors.glassBg,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? colors.darkGlassBorder : colors.glassBorder}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: isDark ? colors.darkGlassBg : colors.glassBg,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? colors.darkGlassBorder : colors.glassBorder}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: isDark ? colors.darkGlassBg : colors.glassBg,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? colors.darkGlassBorder : colors.glassBorder}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: borderRadius.md,
              background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-focused': {
                background: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.xl,
            fontWeight: 600,
            '&.MuiChip-colorPrimary': {
              background: colors.primaryGradient,
              color: '#ffffff',
            },
            '&.MuiChip-colorSecondary': {
              background: colors.secondaryGradient,
              color: '#ffffff',
            },
            '&.MuiChip-colorSuccess': {
              background: colors.successGradient,
              color: '#ffffff',
            },
            '&.MuiChip-colorWarning': {
              background: colors.warningGradient,
              color: '#ffffff',
            },
            '&.MuiChip-colorError': {
              background: colors.errorGradient,
              color: '#ffffff',
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            background: colors.primaryGradient,
            '&:hover': {
              background: colors.primaryGradientHover,
              transform: 'scale(1.1)',
            },
          },
        },
      },
      MuiSpeedDialAction: {
        styleOverrides: {
          fab: {
            background: colors.secondaryGradient,
            '&:hover': {
              background: colors.secondaryGradientHover,
            },
          },
        },
      },
    },
  });
};

// Export theme utilities
export const themeUtils = {
  colors,
  breakpoints,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
};

export default createModernTheme; 